import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ArrowIcon from '../components/ArrowIcon';
import Footer from '../components/Footer';

/* ══════════════════════════════════════════════════════════
   DATA
══════════════════════════════════════════════════════════ */
const SERVICES = [
  {
    num: '01', title: 'IoT Product Development and Engineering',
    desc: 'We create IoT product engineering and design solutions for businesses that want to build smart connected products with the right balance of hardware, software, and functionality.',
    features: ['Hardware architecture planning and system design','PCB schematic capture and component selection','Prototype development and iterative refinement','Design for manufacture (DFM) review','Cross-platform connectivity and protocol planning'],
    color: '#003CFF',
    to: '/iot-product-design-services',
  },
  {
    num: '02', title: 'Embedded Hardware Design and PCB Engineering',
    desc: 'Our embedded hardware and electronic design services help create reliable device-level solutions for connected products and industrial systems.',
    features: ['Schematic capture and multi-layer PCB layout','Signal integrity and power integrity analysis','RF, Bluetooth, and Wi-Fi antenna design','Thermal management and enclosure fit planning','EMI/EMC compliance and regulatory testing support'],
    color: '#C0392B',
    to: '/embedded-hardware-design',
  },
  {
    num: '03', title: 'Embedded Firmware and Software Development',
    desc: 'We offer embedded firmware and software development for smart devices that need stable, efficient, and responsive low-level control and system integration.',
    features: ['RTOS and bare-metal firmware development','Board support package (BSP) and device driver development','Power optimization and low-power sleep management','Secure OTA firmware update implementation','Debug, profiling, and real-time performance tuning'],
    color: '#CC0040',
    to: '/embedded-firmware-development',
  },
  {
    num: '04', title: 'IoT Dashboards, Web and Mobile Applications',
    desc: 'We build custom IoT dashboards and business applications that help businesses monitor devices, track data, and manage connected systems more easily.',
    features: ['Real-time device monitoring and data visualization','Fleet and multi-device management interfaces','Custom alerting, notifications, and trigger rules','Role-based access control and user management','Third-party API and enterprise platform integrations'],
    color: '#5C2DFF',
    to: '/iot-dashboards-web-mobile',
  },
  {
    num: '05', title: 'IoT Cloud Integration and Connectivity',
    desc: 'We provide IoT cloud integration and connectivity services to connect devices, applications, and systems for better control and visibility.',
    features: ['Multi-cloud deployment (AWS IoT, Azure IoT Hub, GCP)','MQTT, REST, WebSocket, and CoAP protocol support','Edge-to-cloud data pipeline design and management','Device identity, provisioning, and lifecycle management','Secure OTA firmware and configuration updates'],
    color: '#006B8F',
    to: '/iot-cloud-integration',
  },
  {
    num: '06', title: 'Research, Innovation and Product R&D',
    desc: 'We provide research, innovation, and product R&D services for businesses that want to explore new ideas, test concepts, and develop future-ready solutions.',
    features: ['Proof-of-concept and feasibility prototyping','Technology assessment and platform selection','Rapid prototyping and iterative concept testing','Innovation documentation and IP-readiness support','Market-ready product development roadmap creation'],
    color: '#1A7A40',
    to: '/research-innovation-rnd',
  },
];

/* Bento layout — area = grid-area name, titlePos decides where the
   label sits. Soft, airy pastel palette ("lightly attractive"); the
   icon continuously tracks the cursor and drifts into whichever
   corner of the card is currently open (pure transform, no resize). */
/* Each tile gets its own `weight` (flex-grow share within its row), so all
   4 tiles render at genuinely different widths — row 1 splits 62/38, row 2
   splits 40/60, giving four distinct sizes instead of two repeated ones. */
const INDUSTRIES = [
  { name:'Healthcare',                  desc:'Patient monitoring, smart diagnostics, connected medical devices', weight:62, bg:'#DDF5E7', fg:'#15151F', icon:'#3FAE74', to:'/industries/healthcare' },
  { name:'Utilities & Energy',          desc:'Smart metering, grid monitoring, energy management systems',       weight:38, bg:'#FCE6D9', fg:'#15151F', icon:'#E0793C', to:'/industries/utilities-energy' },
  { name:'Industrial IoT & Automation', desc:'Factory automation, machine monitoring, process control',          weight:40, bg:'#EAE4FB', fg:'#15151F', icon:'#7C5FD0', to:'/industries/industrial-iot' },
  { name:'Education',                   desc:'STEM kits, robotics labs, cloud-based learning platforms',         weight:60, bg:'#F4E3FB', fg:'#15151F', icon:'#A85AD0', to:'/industries/education' },
];
const INDUSTRY_ROWS = [INDUSTRIES.slice(0, 2), INDUSTRIES.slice(2, 4)];

const PROCESS = [
  {
    num:'01', title:'Discover & Plan',
    desc:'We understand your business goals, define technical requirements, and select the right technology approach for your product.',
    points:['Requirements gathering and stakeholder alignment','Technology stack evaluation and platform selection','System architecture and product roadmap planning','Risk identification and feasibility assessment'],
  },
  {
    num:'02', title:'Design & Build',
    desc:'Our engineers work across hardware, firmware, software, and cloud to develop each layer as a unified, integrated system.',
    points:['Hardware schematic, PCB layout, and prototype build','Firmware and embedded software development','Cloud backend, API, and data pipeline setup','Web and mobile interface development'],
  },
  {
    num:'03', title:'Test & Deploy',
    desc:'We test every layer rigorously before deployment and remain engaged post-launch to ensure long-term reliability and performance.',
    points:['Unit, integration, and system-level testing','Performance profiling and optimization','Staged rollout and production deployment','Post-launch monitoring and ongoing support'],
  },
];

const STATS = [
  { val:'8+',   label:'Years Experience'  },
  { val:'120+', label:'Products Built'    },
  { val:'12',   label:'Industries Served' },
  { val:'45',   label:'Engineers'         },
];

/* ══════════════════════════════════════════════════════════
   HOOK — bidirectional reveal
══════════════════════════════════════════════════════════ */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { setVisible(e.isIntersecting); },
      { threshold: 0.07 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

/* ══════════════════════════════════════════════════════════
   STAT — plain number + label, animated count-up, reveals on scroll
══════════════════════════════════════════════════════════ */
function StatCard({ stat, idx, last, visible }) {
  const target = parseInt(stat.val, 10) || 0;
  const suffix = stat.val.replace(/^[0-9]+/, '');
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!visible) { setCount(0); return; }
    let raf;
    const t0 = performance.now();
    const dur = 1200;
    const tick = (now) => {
      const prog = Math.min(1, (now - t0) / dur);
      setCount(Math.round(target * (1 - Math.pow(1 - prog, 3))));
      if (prog < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, target]);

  const accent = idx % 2 === 0 ? 'var(--blue)' : 'var(--red)';

  return (
    <div style={{
      textAlign:'center',
      padding:'clamp(10px,1.8vw,18px) clamp(10px,1.6vw,20px)',
      borderRight: last ? 'none' : '1px solid rgba(10,10,20,0.08)',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(16px)',
      transition: `opacity 460ms ${idx*90}ms, transform 460ms ${idx*90}ms`,
    }}>
      <div style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(26px,3vw,38px)', color:accent, lineHeight:1, marginBottom:6 }}>
        {count}{suffix}
      </div>
      <div style={{ fontSize:10.5, fontWeight:700, color:'var(--ink-soft)', letterSpacing:3, textTransform:'uppercase', fontFamily:"'Inter', sans-serif" }}>
        {stat.label}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   INDUSTRY ICONS
══════════════════════════════════════════════════════════ */
function IndustryIcon({ name, idx, color, size }) {
  const c = color || (idx % 2 === 0 ? '#003CFF' : '#FF0004');
  const s = size || 26;
  if (name==='Healthcare') return <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={c} strokeWidth="1.5"/><path d="M12 8v8M8 12h8" stroke={c} strokeWidth="2" strokeLinecap="round"/></svg>;
  if (name==='Utilities & Energy') return <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke={c} strokeWidth="1.5" strokeLinejoin="round"/></svg>;
  if (name==='Industrial IoT & Automation') return <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke={c} strokeWidth="1.5"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5.64 5.64l2.12 2.12M16.24 16.24l2.12 2.12M5.64 18.36l2.12-2.12M16.24 7.76l2.12-2.12" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>;
  if (name==='Education') return <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 3L2 8l10 5 10-5-10-5z" stroke={c} strokeWidth="1.5" strokeLinejoin="round"/><path d="M6 10.6V16c0 1.66 2.69 3 6 3s6-1.34 6-3v-5.4" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>;
  return <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="7" y="2" width="10" height="20" rx="2.5" stroke={c} strokeWidth="1.5"/><circle cx="12" cy="18" r="1.5" fill={c}/><path d="M9 6h6" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>;
}

/* ══════════════════════════════════════════════════════════
   PROCESS STEP VISUALS — AI-generated illustrations
══════════════════════════════════════════════════════════ */
const PROCESS_VISUALS = ['/2one.png', '/3one.png', '/lastone.png'];

function ProcessStepVisual({ stepIdx, visualRef }) {
  return (
    <img
      ref={visualRef}
      src={PROCESS_VISUALS[stepIdx]}
      alt={PROCESS[stepIdx].title}
      style={{ width:'100%', maxWidth:460, display:'block', borderRadius:20 }}
    />
  );
}

/* ══════════════════════════════════════════════════════════
   VISUAL EDGE ANCHORS
   — given the two illustration elements, returns the true top-/
     bottom-center touch-points of their on-screen boxes (via
     getBoundingClientRect, which already accounts for every
     transform/animation in play) — so a connector drawn between
     them visually lands flush against each visual, from any side,
     at any viewport size
══════════════════════════════════════════════════════════ */
function getVisualEdgeAnchors(fromEl, toEl, wrapEl) {
  if (!fromEl || !toEl || !wrapEl) return null;
  const wrap = wrapEl.getBoundingClientRect();

  const edgePoint = (el, atBottom) => {
    const rect = el.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) return null;
    return {
      x: rect.left + rect.width / 2 - wrap.left,
      y: (atBottom ? rect.bottom : rect.top) - wrap.top,
    };
  };

  const from = edgePoint(fromEl, true);
  const to   = edgePoint(toEl, false);
  if (!from || !to) return null;
  return { x1: from.x, y1: from.y, x2: to.x, y2: to.y };
}

/* ══════════════════════════════════════════════════════════
   CONNECTOR LINE
   — enabled prop: when false, progress is locked at 0
     (connector 2 only starts counting after step 2 reveals)
   — fromRef/toRef point at each stage's illustration <img>; the
     path is drawn between their *measured visible edges* so it
     physically touches each visual, top/bottom-center, no guesswork
   — as the user scrolls, the line draws itself in, a glowing point
     travels along its leading edge toward the next visual, and right
     before it lands, a pulse ring blooms on the destination icon just
     as that step's content reveals — progress is paced so all three
     of these culminate as that destination reaches the screen's
     vertical middle (its natural focal point), not its very top edge
══════════════════════════════════════════════════════════ */
function ConnectorLine({ onProgress, enabled = true, fromRef, toRef }) {
  const wrapRef = useRef(null);
  const pathRef = useRef(null);
  const lastAnchors = useRef(null);

  const [pathLen,  setPathLen] = useState(0);
  const [progress, setProgress]= useState(0);
  const [anchors,  setAnchors] = useState(null); // { x1,y1, x2,y2 } — wrapper-relative
  const [tip,      setTip]     = useState(null); // travelling point along the leading edge

  const H = 110; // connector spacer height between stages — actual path follows measured anchors

  const measure = () => {
    if (!wrapRef.current || !fromRef?.current || !toRef?.current) return;
    const next = getVisualEdgeAnchors(fromRef.current, toRef.current, wrapRef.current);
    if (!next) return;
    const prev = lastAnchors.current;
    if (!prev || Math.abs(prev.x1-next.x1)>0.5 || Math.abs(prev.y1-next.y1)>0.5 ||
        Math.abs(prev.x2-next.x2)>0.5 || Math.abs(prev.y2-next.y2)>0.5) {
      lastAnchors.current = next;
      setAnchors(next);
    }
  };

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current)    ro.observe(wrapRef.current);
    if (fromRef?.current)   ro.observe(fromRef.current);
    if (toRef?.current)     ro.observe(toRef.current);
    window.addEventListener('resize', measure);
    /* illustrations animate in (scale/translate) on reveal — re-measure
       repeatedly while that transition plays so the path locks onto
       their settled position without lagging behind mid-animation */
    const ticks = [80, 160, 260, 380, 520, 700, 900];
    const timers = ticks.map(ms => setTimeout(measure, ms));
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); timers.forEach(clearTimeout); };
  }, []);

  useEffect(() => {
    if (pathRef.current && anchors) {
      try { setPathLen(pathRef.current.getTotalLength()); } catch(_) {}
    }
  }, [anchors]);

  useEffect(() => {
    const onScroll = () => {
      if (!wrapRef.current) return;
      measure();
      /* when gated, hold at 0 so the line doesn't animate prematurely */
      if (!enabled) {
        setProgress(0);
        if (onProgress) onProgress(0);
        return;
      }
      const a = lastAnchors.current;
      if (!a) return;
      /* pace progress by the destination touch-point's position on screen —
         0 as it crosses into view at the bottom edge, 1 once it reaches the
         viewport's vertical middle. That way the line finishes drawing, the
         travelling point lands, the bloom fires, and the content reveals
         all right as that step becomes the centred focus of the screen —
         not while it's still pinned up near the browser chrome */
      const wrapTop   = wrapRef.current.getBoundingClientRect().top;
      const destY     = wrapTop + a.y2;
      const vh        = window.innerHeight;
      const enterAt   = vh;       // bottom edge — journey starts
      const arriveAt  = vh * 0.5; // vertical middle — journey ends
      const prog = Math.max(0, Math.min(1, (enterAt - destY) / (enterAt - arriveAt)));
      setProgress(prog);
      if (onProgress) onProgress(prog);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [onProgress, enabled]);

  /* the travelling point rides the leading edge of the drawn line — a
     live focal point of motion pushing toward the next visual */
  useEffect(() => {
    if (!pathRef.current || !pathLen || progress <= 0.005 || progress >= 0.992) {
      setTip(null);
      return;
    }
    try {
      const pt = pathRef.current.getPointAtLength(pathLen * progress);
      setTip({ x: pt.x, y: pt.y });
    } catch(_) { setTip(null); }
  }, [progress, pathLen]);

  if (!anchors) return <div ref={wrapRef} style={{ width:'100%', height:H }}/>;

  const { x1, y1, x2, y2 } = anchors;

  /* Single smooth S-curve directly between the two measured anchor points —
     clean zig-zag rhythm, no sharp turns, physically touches both visuals */
  const midY = (y1 + y2) / 2;
  const d = `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;

  /* the destination icon "lights up" in the final stretch — arriving
     just ahead of that step's content reveal (gated at 0.92 below), so
     the bloom announces the step a beat before the line actually lands */
  const arriveT = Math.max(0, Math.min(1, (progress - 0.78) / 0.18));

  return (
    <div ref={wrapRef} style={{ width:'100%', height:H, position:'relative', zIndex:2 }}>
      <svg width="100%" height={H} style={{ position:'absolute', left:0, top:0, overflow:'visible' }}>
        {/* the navigating line — draws itself in as the user scrolls,
            travelling from one visual toward the next, touching both */}
        <path ref={pathRef} d={d}
          stroke="var(--blue)" strokeWidth="2.5" fill="none"
          strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray={pathLen || 9999}
          strokeDashoffset={pathLen ? pathLen * (1 - progress) : 9999}/>

        {/* travelling point — a bright focal point of motion riding the
            leading edge of the line, with a soft pulsing halo around it */}
        {tip && (
          <g style={{ opacity: Math.min(1, progress * 14) }}>
            <circle cx={tip.x} cy={tip.y} r="6.5" fill="var(--blue)" fillOpacity="0.18" className="iot-conn-head-halo"/>
            <circle cx={tip.x} cy={tip.y} r="3.2" fill="var(--blue)"/>
            <circle cx={tip.x} cy={tip.y} r="1.3" fill="#fff"/>
          </g>
        )}

        {/* arrival bloom — rings out from the destination touch-point in
            the final approach, announcing the step right before it lands */}
        {arriveT > 0 && (
          <g style={{ opacity: arriveT }}>
            <circle cx={x2} cy={y2} r="2.6" fill="var(--blue)"/>
            <circle cx={x2} cy={y2} r="6" fill="none" stroke="var(--blue)" strokeWidth="1.4" className="iot-conn-arrive-ring"/>
            <circle cx={x2} cy={y2} r="6" fill="none" stroke="var(--blue)" strokeWidth="1.4" className="iot-conn-arrive-ring" style={{ animationDelay:'0.55s' }}/>
          </g>
        )}
      </svg>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   PROCESS STEP BLOCK
   — visibility is fully controlled by parent (revealed prop)
══════════════════════════════════════════════════════════ */
function ProcessStepBlock({ step, stepIdx, imageLeft, revealed, dimmed = false, visualRef }) {
  /* three states: hidden (not revealed) → current (revealed, !dimmed) → completed (dimmed) */
  const emphasis = !revealed ? 0 : (dimmed ? 0.55 : 1);

  const illustration = (
    /* the reveal/scale animation lives right on this div; visualRef sits
       directly on the illustration <img>, so connector lines measure its
       live, on-screen box via getBoundingClientRect and always land
       flush against it */
    <div style={{
      display:'block', lineHeight:0,
      opacity: emphasis,
      transform: !revealed
        ? 'scale(0.84) translateY(36px)'
        : (dimmed ? 'scale(0.94) translateY(0)' : 'scale(1) translateY(0)'),
      transition:'opacity 520ms cubic-bezier(.2,.8,.2,1), transform 520ms cubic-bezier(.2,.8,.2,1)',
    }}>
      <ProcessStepVisual stepIdx={stepIdx} visualRef={visualRef}/>
    </div>
  );

  const content = (
    <div style={{
      display:'flex', flexDirection:'column', justifyContent:'center',
      opacity: emphasis,
      transform: !revealed
        ? `translateX(${imageLeft ? '32px' : '-32px'})`
        : 'translateX(0)',
      transition:'opacity 480ms cubic-bezier(.2,.8,.2,1) 80ms, transform 480ms cubic-bezier(.2,.8,.2,1) 80ms',
    }}>
      <div style={{
        display:'inline-flex', alignItems:'center', gap:8,
        padding:'4px 12px',
        background:'rgba(0,60,255,0.07)',
        border:'1px solid rgba(0,60,255,0.18)',
        borderRadius:999, marginBottom:18,
        width:'fit-content',
      }}>
        <span style={{ width:4, height:4, borderRadius:'50%', background:'var(--blue)', flexShrink:0 }}/>
        <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:9.5, letterSpacing:3, color:'var(--blue)', textTransform:'uppercase' }}>
          STEP {step.num} OF {PROCESS.length}
        </span>
      </div>

      <h3 style={{
        fontFamily:"'Anton',sans-serif",
        fontWeight:400,
        fontSize:'clamp(26px,3.5vw,42px)',
        lineHeight:1.08,
        color:'var(--ink)', marginBottom:16,
      }}>
        {step.title}
      </h3>

      <p style={{
        fontSize:15.5, lineHeight:1.85,
        color:'var(--ink-soft)', marginBottom:28,
        maxWidth:440,
      }}>
        {step.desc}
      </p>

      <div style={{ width:36, height:2, background:'var(--red)', borderRadius:2, marginBottom:24, opacity:0.6 }}/>

      <ul style={{ listStyle:'none', padding:0, margin:0 }}>
        {step.points.map((pt, j) => (
          <li key={j} style={{
            display:'flex', alignItems:'flex-start', gap:13,
            paddingTop:    j > 0 ? 13 : 0,
            paddingBottom: j < step.points.length - 1 ? 13 : 0,
            borderTop:     j > 0 ? '1px solid rgba(10,10,20,0.06)' : 'none',
            opacity:   revealed ? 1 : 0,
            transform: revealed ? 'translateY(0)' : 'translateY(14px)',
            transition:`opacity 460ms ease ${j*80+320}ms, transform 460ms ease ${j*80+320}ms`,
          }}>
            <div style={{
              width:18, height:18, borderRadius:5,
              background:'rgba(0,60,255,0.08)', border:'1px solid rgba(0,60,255,0.20)',
              display:'flex', alignItems:'center', justifyContent:'center',
              flexShrink:0, marginTop:2,
            }}>
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="var(--blue)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span style={{ fontSize:14.5, lineHeight:1.65, color:'var(--ink-soft)', fontWeight:500 }}>{pt}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="iot-proc-step-grid" style={{
      minHeight:'clamp(280px, 40vh, 400px)',
      alignItems:'flex-start',
      paddingTop:'clamp(20px,3vh,36px)',
    }}>
      {imageLeft ? <>{illustration}{content}</> : <>{content}{illustration}</>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   PROCESS SECTION
   — step 1 revealed when section enters viewport
   — steps 2 & 3 revealed only when connector line arrives
   — everything reverses on scroll-up
══════════════════════════════════════════════════════════ */
function ProcessSection() {
  const [headRef, headVisible] = useReveal();

  /* refs to each stage's illustration <img> — connectors measure their
     true on-screen box directly so the path physically touches the
     visual itself (top/bottom-center) */
  const visualRef0 = useRef(null);
  const visualRef1 = useRef(null);
  const visualRef2 = useRef(null);

  const step1Ref = useRef(null);
  const [step1Visible, setStep1Visible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => setStep1Visible(e.isIntersecting),
      { threshold: 0.08 }
    );
    if (step1Ref.current) obs.observe(step1Ref.current);
    return () => obs.disconnect();
  }, []);

  const [conn1Prog, setConn1Prog] = useState(0);
  const [conn2Prog, setConn2Prog] = useState(0);

  /* trigger the next step's reveal right at the tail end of its connector's
     journey — a beat before the line actually lands on the visual (which
     happens at progress 1), so the "arriving" content and the "arriving"
     line read as one connected motion rather than the content jumping
     in early while the line is still well short of touching it */
  const step2Visible = conn1Prog > 0.92;
  const step3Visible = conn2Prog > 0.92;

  return (
    <section style={{ background:'transparent' }}>

      <div ref={headRef} style={{
        maxWidth:1280, margin:'0 auto',
        padding:'clamp(56px,7vw,92px) clamp(20px,5vw,48px) clamp(40px,5vw,60px)',
        opacity:   headVisible ? 1 : 0,
        transform: headVisible ? 'translateY(0)' : 'translateY(22px)',
        transition:'opacity 520ms, transform 520ms',
      }}>
        <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:10.5, letterSpacing:3, color:'var(--red)', textTransform:'uppercase' }}>
          HOW WE WORK
        </span>
        <h2 style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(28px,4vw,48px)', lineHeight:1.07, marginTop:8, marginBottom:10 }}>
          Our{' '}
          <span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:'var(--blue)' }}>Process</span>
        </h2>
        <p style={{ fontSize:15, color:'var(--ink-soft)', maxWidth:500, lineHeight:1.78 }}>
          From your first idea to a live, production-ready product — here is how we get there together.
        </p>
      </div>

      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px) clamp(28px,4vw,48px)' }}>

        <div ref={step1Ref}>
          <ProcessStepBlock step={PROCESS[0]} stepIdx={0} imageLeft={true} revealed={step1Visible} dimmed={conn1Prog > 0.5} visualRef={visualRef0}/>
        </div>

        <ConnectorLine onProgress={setConn1Prog} fromRef={visualRef0} toRef={visualRef1}/>

        <ProcessStepBlock step={PROCESS[1]} stepIdx={1} imageLeft={false} revealed={step2Visible} dimmed={conn2Prog > 0.5} visualRef={visualRef1}/>

        {/* enabled gates connector 2: only starts counting after step 2 has revealed */}
        <ConnectorLine onProgress={setConn2Prog} enabled={step2Visible} fromRef={visualRef1} toRef={visualRef2}/>

        <ProcessStepBlock step={PROCESS[2]} stepIdx={2} imageLeft={true} revealed={step3Visible} visualRef={visualRef2}/>

      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════
   PAGE
══════════════════════════════════════════════════════════ */
export default function IoTSolutionsPage() {
  const [activeService, setActiveService] = useState(0);
  const [panelKey,      setPanelKey]      = useState(0);
  const autoTimerRef = useRef(null);
  const AUTO_INTERVAL = 5200;

  const [heroRef,  heroVisible]  = useReveal();
  const [statsRef, statsVisible] = useReveal();
  const [tabRef,   tabVisible]   = useReveal();
  const [indRef,   indVisible]   = useReveal();
  const [whyRef,   whyVisible]   = useReveal();
  const [ctaRef,   ctaVisible]   = useReveal();

  /* Bento icons track the cursor directly via the DOM (no setState
     mid-move — keeps it at 60fps) and drift toward whichever corner
     of the card is currently free, i.e. away from the pointer. */
  const bentoIconRefs = useRef([]);
  const handleBentoMove = (e, i) => {
    const el = bentoIconRefs.current[i];
    if (!el) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width  - 0.5;  // -0.5 .. 0.5
    const py = (e.clientY - rect.top)  / rect.height - 0.5;
    el.style.transform = `translate(${(-px * 34).toFixed(1)}px, ${(-py * 34).toFixed(1)}px) scale(1.12)`;
  };
  const handleBentoLeave = (i) => {
    const el = bentoIconRefs.current[i];
    if (el) el.style.transform = 'translate(0px,0px) scale(1)';
  };

  /* services panel auto-advances on a timer; any manual click restarts
     the timer so the auto-rotation never fights the user's choice */
  const restartAutoRotate = () => {
    clearInterval(autoTimerRef.current);
    autoTimerRef.current = setInterval(() => {
      setActiveService(prev => (prev + 1) % SERVICES.length);
      setPanelKey(k => k + 1);
    }, AUTO_INTERVAL);
  };
  useEffect(() => {
    restartAutoRotate();
    return () => clearInterval(autoTimerRef.current);
  }, []);

  function handleService(idx) { setPanelKey(k => k + 1); setActiveService(idx); restartAutoRotate(); }
  const active = SERVICES[activeService];

  return (
    <>
      <Helmet>
        <title>IoT Solutions &amp; Product Engineering Services — Zone-X</title>
        <meta name="description" content="Zone-X provides IoT product engineering, embedded hardware, firmware development, PCB design, custom web and mobile applications, and R&D services in Bangalore, Karnataka, and across India."/>
        <link rel="canonical" href="https://zone-x.dev/iot-solutions"/>
      </Helmet>

      <style>{`
        @keyframes iotFlow { to { stroke-dashoffset: -18; } }
        @keyframes iotTabIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .iot-data-line { stroke-dasharray: 5,4; animation: iotFlow 2.4s linear infinite; }
        .iot-tab-panel { animation: iotTabIn 260ms ease both; }

        /* Process connector — travelling point & arrival bloom */
        @keyframes iotConnHalo {
          0%, 100% { transform: scale(1);    opacity: 0.55; }
          50%      { transform: scale(1.85); opacity: 1;    }
        }
        @keyframes iotConnArrive {
          0%   { transform: scale(0.5); opacity: 0.95; }
          75%  { transform: scale(2.8); opacity: 0;    }
          100% { transform: scale(2.8); opacity: 0;    }
        }
        .iot-conn-head-halo {
          transform-box: fill-box; transform-origin: center;
          animation: iotConnHalo 1.05s ease-in-out infinite;
        }
        .iot-conn-arrive-ring {
          transform-box: fill-box; transform-origin: center;
          animation: iotConnArrive 1.4s ease-out infinite;
        }

        /* Process grid (inner 2-col card layout) */
        .iot-proc-step-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(36px, 5vw, 80px);
          align-items: start;
        }
        @media (max-width: 760px) {
          .iot-proc-step-grid { grid-template-columns: 1fr; gap: 28px; }
        }


        /* Hero */
        .iot-hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(32px,5vw,64px);
          align-items: center;
        }
        @media (max-width: 820px) {
          .iot-hero-grid { grid-template-columns: 1fr; }
          .iot-hero-visual { display: none !important; }
        }

        /* Services tab layout */
        .iot-services-layout {
          display: grid;
          grid-template-columns: 316px 1fr;
          border: 1px solid rgba(10,10,20,0.09);
          border-radius: 20px;
          overflow: hidden;
          background: rgba(255,255,255,0.88);
        }
        @media (max-width: 900px) {
          .iot-services-layout { grid-template-columns: 1fr; }
          .iot-services-nav { border-right: none !important; border-bottom: 1px solid rgba(10,10,20,0.08); }
        }

        /* Industries — bento grid: mixed sizes & positions, à la Zoho */
        .iot-bento-grid {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .iot-bento-row {
          display: flex;
          gap: 14px;
        }
        .iot-bento-row .iot-bento-card {
          min-height: 190px;
        }
        @media (max-width: 560px) {
          .iot-bento-row {
            flex-direction: column;
          }
          .iot-bento-row .iot-bento-card {
            flex: 1 1 100% !important;
          }
        }
        .iot-bento-card {
          position: relative;
          overflow: hidden;
          border-radius: 20px;
          padding: clamp(20px,2.4vw,28px);
          display: flex;
          flex-direction: column;
          cursor: pointer;
        }
        .iot-bento-card:hover {
          box-shadow: 0 18px 40px rgba(10,10,20,0.16);
        }
        .iot-bento-icon {
          transition: transform 220ms cubic-bezier(.22,1,.36,1), opacity 320ms ease;
          will-change: transform;
        }
        .iot-bento-card:hover .iot-bento-icon { opacity: 1; }
        .iot-bento-arrow {
          position: absolute; top: 18px; right: 18px;
          width: 30px; height: 30px; border-radius: 999px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.14);
          opacity: 0; transform: translate(-6px, 6px) rotate(-45deg);
          transition: opacity 280ms ease, transform 360ms cubic-bezier(.34,1.56,.64,1);
        }
        .iot-bento-card:hover .iot-bento-arrow {
          opacity: 1; transform: translate(0,0) rotate(0deg);
        }

        /* Why choose */
        .iot-why-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(40px,6vw,88px);
          align-items: start;
        }
        @media (max-width: 820px) { .iot-why-grid { grid-template-columns: 1fr; } }

        /* Stats — compact row, plain numbers with thin dividers */
        .iot-stats-row { display: grid; grid-template-columns: repeat(4,1fr); }
        @media (max-width: 620px) { .iot-stats-row { grid-template-columns: repeat(2,1fr); } }

        /* Nav buttons */
        .iot-nav-btn {
          width:100%; text-align:left; display:flex; align-items:flex-start;
          gap:12px; padding:16px 20px;
          border-bottom: 1px solid rgba(10,10,20,0.06);
          background:transparent; transition:background 160ms;
        }
        .iot-nav-btn:hover { background: rgba(0,60,255,0.04); }

        /* Services auto-advance progress sliver — fills over the rotation interval */
        .iot-auto-progress {
          position:absolute; left:0; right:0; bottom:0; height:2px;
          transform-origin:left; transform:scaleX(0);
          animation: iotAutoFill 5200ms linear forwards;
        }
        @keyframes iotAutoFill { to { transform: scaleX(1); } }

        /* Industry cells */
      `}</style>

      {/* ══════════════ HERO ══════════════ */}
      <section style={{ background:'transparent', paddingTop:120, paddingBottom:80, position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, pointerEvents:'none', backgroundImage:'radial-gradient(rgba(10,10,20,0.032) 1px, transparent 1px)', backgroundSize:'28px 28px' }}/>
        <div style={{ position:'absolute', top:-140, right:'3%', width:680, height:680, borderRadius:'50%', background:'radial-gradient(circle, rgba(0,60,255,0.09) 0%, transparent 60%)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-80, left:'5%', width:400, height:400, borderRadius:'50%', background:'radial-gradient(circle, rgba(255,0,4,0.07) 0%, transparent 65%)', pointerEvents:'none' }}/>

        <div ref={heroRef} className="iot-hero-grid" style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)', position:'relative', zIndex:1 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:36, opacity:heroVisible?1:0, transform:heroVisible?'none':'translateY(14px)', transition:'opacity 480ms, transform 480ms' }}>
              <Link to="/" style={{ fontSize:11, fontWeight:600, color:'var(--ink-soft)', letterSpacing:'0.04em', fontFamily:"'Inter', sans-serif", textTransform:'uppercase' }}>Home</Link>
              <span style={{ color:'rgba(10,10,20,0.22)', fontSize:11 }}>/</span>
              <span style={{ fontSize:11, fontWeight:600, color:'var(--ink)', letterSpacing:'0.04em', fontFamily:"'Inter', sans-serif", textTransform:'uppercase' }}>IoT Solutions</span>
            </div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 14px', background:'rgba(255,0,4,0.06)', border:'1px solid rgba(255,0,4,0.18)', borderRadius:999, marginBottom:22, opacity:heroVisible?1:0, transform:heroVisible?'none':'translateY(16px)', transition:'opacity 480ms 60ms, transform 480ms 60ms' }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:'var(--red)', flexShrink:0 }}/>
              <span style={{ fontSize:10.5, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color:'var(--red)', fontFamily:"'Inter', sans-serif" }}>Services / IoT Solutions</span>
            </div>
            <h1 style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(36px,5vw,64px)', lineHeight:1.06, color:'var(--ink)', marginBottom:20, opacity:heroVisible?1:0, transform:heroVisible?'none':'translateY(22px)', transition:'opacity 520ms 120ms, transform 520ms 120ms' }}>
              Smart IoT &{' '}
              <span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:'var(--red)' }}>Product</span>
              <br/>Development Solutions
            </h1>
            <p style={{ fontSize:'clamp(15px,1.5vw,17px)', lineHeight:1.82, color:'var(--ink-soft)', maxWidth:540, marginBottom:12, opacity:heroVisible?1:0, transform:heroVisible?'none':'translateY(18px)', transition:'opacity 520ms 180ms, transform 520ms 180ms' }}>
              Every successful product begins with the right engineering foundation. At Zone-X, we help businesses accelerate innovation through product engineering, embedded systems, IoT technologies, and software development — all under one roof.
            </p>
            <p style={{ fontSize:12.5, lineHeight:1.7, color:'rgba(10,10,20,0.36)', maxWidth:460, marginBottom:36, fontFamily:"'Inter', sans-serif", opacity:heroVisible?1:0, transform:heroVisible?'none':'translateY(14px)', transition:'opacity 520ms 230ms, transform 520ms 230ms' }}>
              Serving businesses in Bangalore, Karnataka, and across India.
            </p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:12, opacity:heroVisible?1:0, transform:heroVisible?'none':'translateY(14px)', transition:'opacity 520ms 280ms, transform 520ms 280ms' }}>
              <Link to="/contact" style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'13px 26px', background:'linear-gradient(135deg, var(--red), #cc0003)', color:'white', borderRadius:999, fontSize:13, fontWeight:700,  boxShadow:'0 8px 24px rgba(255,0,4,0.35)' }}>
                Get In Touch <ArrowIcon size={12}/>
              </Link>
              <a href="#our-services" style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'13px 26px', background:'rgba(255,255,255,0.82)', border:'1.5px solid rgba(10,10,20,0.12)', color:'var(--ink)', borderRadius:999, fontSize:13, fontWeight:600,  }}>
                Explore Services <ArrowIcon size={12}/>
              </a>
            </div>
          </div>
          <div className="iot-hero-visual" style={{ display:'flex', justifyContent:'center', opacity:heroVisible?1:0, transform:heroVisible?'none':'translateY(24px) scale(0.96)', transition:'opacity 700ms 300ms, transform 700ms 300ms' }}>
            <img src="/first1.png" alt="Zone-X IoT network — connected devices syncing in real time" style={{ width:'100%', maxWidth:480, display:'block' }}/>
          </div>
        </div>
      </section>

      {/* ══════════════ STATS ══════════════ */}
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'clamp(20px,3vw,32px) clamp(20px,5vw,48px)' }}>
        <div ref={statsRef} className="iot-stats-row">
          {STATS.map((stat,i) => (
            <StatCard key={stat.label} stat={stat} idx={i} last={i === STATS.length - 1} visible={statsVisible}/>
          ))}
        </div>
      </div>

      {/* ══════════════ PROCESS ══════════════ */}
      <ProcessSection/>

      {/* ══════════════ SERVICES TAB ══════════════ */}
      <section id="our-services" style={{ background:'transparent', padding:'clamp(56px,7vw,92px) 0' }}>
        <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
          <div ref={tabRef} style={{ marginBottom:44, opacity:tabVisible?1:0, transform:tabVisible?'none':'translateY(18px)', transition:'opacity 480ms, transform 480ms' }}>
            <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:10.5, letterSpacing:3, color:'var(--red)', textTransform:'uppercase' }}>WHAT WE DO</span>
            <h2 style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(28px,4vw,48px)', lineHeight:1.07, marginTop:8, marginBottom:10 }}>
              Our{' '}<span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:'var(--red)' }}>Services</span>
            </h2>
            <p style={{ fontSize:15, color:'var(--ink-soft)', maxWidth:540, lineHeight:1.78 }}>
              Eleven focused service areas spanning IoT engineering, embedded systems, and software development — all delivered by one integrated team.
            </p>
          </div>

          <div className="iot-services-layout" style={{ opacity:tabVisible?1:0, transition:'opacity 560ms 120ms' }}>
            <div className="iot-services-nav" style={{ background:'rgba(255,255,255,0.65)', borderRight:'1px solid rgba(10,10,20,0.08)' }}>
              {SERVICES.map((s,i) => {
                const isActive = i === activeService;
                return (
                  <button key={s.num} onClick={() => handleService(i)} className="iot-nav-btn"
                    style={{ position:'relative', borderLeft:`3px solid ${isActive?s.color:'transparent'}`, background:isActive?'rgba(255,255,255,0.9)':undefined }}>
                    <span style={{ fontFamily:"'Anton', sans-serif", fontWeight:400, fontSize:10, color:isActive?s.color:'rgba(10,10,20,0.28)', flexShrink:0, paddingTop:2 }}>{s.num}</span>
                    <span style={{ fontSize:14, fontWeight:isActive?700:500, color:isActive?'var(--ink)':'var(--ink-soft)', lineHeight:1.38 }}>{s.title}</span>
                    {isActive && <span key={panelKey} className="iot-auto-progress" style={{ background:s.color }}/>}
                  </button>
                );
              })}
            </div>

            <div key={panelKey} className="iot-tab-panel" style={{ padding:'clamp(28px,4vw,56px)', display:'flex', flexDirection:'column', justifyContent:'center' }}>
              <div style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:10, color:active.color, letterSpacing:2.5, textTransform:'uppercase', marginBottom:14 }}>SERVICE {active.num}</div>
              <h3 style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(22px,3vw,36px)', lineHeight:1.1, color:'var(--ink)', marginBottom:18 }}>{active.title}</h3>
              <p style={{ fontSize:15.5, lineHeight:1.85, color:'var(--ink-soft)', marginBottom:32, maxWidth:520 }}>{active.desc}</p>
              <div style={{ width:40, height:2, background:active.color, borderRadius:2, marginBottom:28, opacity:0.55 }}/>
              <ul style={{ listStyle:'none', padding:0, margin:0 }}>
                {active.features.map((feat,i) => (
                  <li key={i} style={{ display:'flex', alignItems:'flex-start', gap:14, paddingTop:i>0?15:0, paddingBottom:i<active.features.length-1?15:0, borderTop:i>0?'1px solid rgba(10,10,20,0.06)':'none' }}>
                    <div style={{ width:20, height:20, borderRadius:6, background:`${active.color}12`, border:`1px solid ${active.color}28`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke={active.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <span style={{ fontSize:14, lineHeight:1.65, color:'var(--ink-soft)', fontWeight:500 }}>{feat}</span>
                  </li>
                ))}
              </ul>
              <div style={{ marginTop:36, display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
                <Link to="/contact" style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'11px 22px', background:active.color, color:'white', borderRadius:999, fontSize:13, fontWeight:700,  boxShadow:`0 6px 20px ${active.color}30` }}>
                  Discuss This Service <ArrowIcon size={11}/>
                </Link>
                {active.to && (
                  <Link to={active.to} style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'11px 22px', background:'rgba(255,255,255,0.9)', border:`1.5px solid ${active.color}50`, color:active.color, borderRadius:999, fontSize:13, fontWeight:600,  }}>
                    View Service <ArrowIcon size={11}/>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ INDUSTRIES ══════════════ */}
      <section style={{ background:'transparent', padding:'clamp(56px,7vw,92px) 0' }}>
        <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
          <div ref={indRef}>
            <div style={{ marginBottom:44, opacity:indVisible?1:0, transform:indVisible?'none':'translateY(18px)', transition:'opacity 480ms, transform 480ms' }}>
              <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:10.5, letterSpacing:3, color:'var(--blue)', textTransform:'uppercase' }}>INDUSTRIES</span>
              <h2 style={{ fontFamily:"'Anton',sans-serif", fontWeight:400, fontSize:'clamp(28px,4vw,48px)', lineHeight:1.07, marginTop:8, marginBottom:10 }}>
                Industries We{' '}<span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:'var(--red)' }}>Serve</span>
              </h2>
              <p style={{ fontSize:15, color:'var(--ink-soft)', maxWidth:580, lineHeight:1.78 }}>
                Zone-X delivers IoT solutions across four industries — healthcare, utilities &amp; energy, industrial IoT &amp; automation, and education.
              </p>
            </div>
            <div className="iot-bento-grid">
              {INDUSTRY_ROWS.map((row, ri) => (
                <div className="iot-bento-row" key={ri}>
                  {row.map((ind, ci) => {
                    const i = ri * 2 + ci;
                    const big = ind.weight >= 50;
                    return (
                      <Link
                        key={ind.name}
                        to={ind.to}
                        className="iot-bento-card hoverable"
                        onMouseMove={(e) => handleBentoMove(e, i)}
                        onMouseLeave={() => handleBentoLeave(i)}
                        style={{
                          flex: `${ind.weight} 1 0`,
                          background: ind.bg,
                          color: ind.fg,
                          textDecoration: 'none',
                          justifyContent: 'space-between',
                          opacity: indVisible ? 1 : 0,
                          transform: indVisible ? 'none' : 'translateY(18px)',
                          transitionProperty: 'opacity, transform, box-shadow',
                          transitionDuration: '520ms, 520ms, 360ms',
                          transitionDelay: `${i * 60}ms, ${i * 60}ms, 0ms`,
                        }}
                      >
                        <div className="iot-bento-arrow" style={{ color: ind.fg }}>
                          <ArrowIcon size={13} />
                        </div>

                        <div
                          ref={(el) => { bentoIconRefs.current[i] = el; }}
                          className="iot-bento-icon"
                          style={{
                            flex:1, display:'flex', alignItems:'center', justifyContent:'center',
                            margin: '6px 0',
                            opacity: 0.7,
                            transform: 'translate(0px,0px) scale(1)',
                          }}
                        >
                          <IndustryIcon name={ind.name} idx={i} color={ind.icon} size={big ? 84 : 56} />
                        </div>

                        <div>
                          <div style={{ fontSize:big ? 22 : 16, fontWeight:800, lineHeight:1.28 }}>{ind.name}</div>
                          <div style={{ fontSize:big ? 14 : 12, fontWeight:500, lineHeight:1.4, opacity:0.7, marginTop:5, maxWidth:big ? 320 : 'none' }}>{ind.desc}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ WHY CHOOSE — section commented out ══════════════
      <section style={{ background:'transparent', padding:'clamp(64px,8vw,104px) 0' }}>
        <div ref={whyRef} style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
          <div className="iot-why-grid">
            <div style={{ opacity:whyVisible?1:0, transform:whyVisible?'none':'translateY(24px)', transition:'opacity 520ms, transform 520ms' }}>
              <span style={{ fontFamily:"'Anton', sans-serif", fontSize:10.5, color:'var(--red)', textTransform:'uppercase' }}>WHY ZONE-X</span>
              <h2 style={{ fontFamily:"'Anton',sans-serif", fontSize:'clamp(30px,4.5vw,56px)', lineHeight:1.05, color:'var(--ink)', marginTop:10, marginBottom:22 }}>
                One team.<br/><span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:'var(--red)' }}>Every layer.</span>
              </h2>
              <p style={{ fontSize:15.5, lineHeight:1.88, color:'var(--ink-soft)', marginBottom:20 }}>
                Businesses choose Zone-X because we combine IoT, hardware, software, embedded systems, and product R&D in one place. Fewer handoffs. Better communication. A smoother product journey.
              </p>
              <p style={{ fontSize:15.5, lineHeight:1.88, color:'var(--ink-soft)' }}>
                Whether you need IoT product engineering in Bangalore, custom IoT design in Karnataka, or a long-term embedded systems partner — Zone-X is ready.
              </p>
            </div>
            <div style={{ opacity:whyVisible?1:0, transform:whyVisible?'none':'translateY(24px)', transition:'opacity 520ms 100ms, transform 520ms 100ms' }}>
              {[
                { num:'01', title:'End-to-end under one roof',   desc:'Hardware, firmware, embedded software, cloud, and mobile — all handled internally without third-party handoffs.' },
                { num:'02', title:'Deep technical expertise',     desc:'Engineers with real hands-on experience across embedded systems, IoT protocols, PCB design, and cloud architecture.' },
                { num:'03', title:'Business-aligned engineering', desc:'We focus on solving real business problems, not just building technically impressive systems that miss the goal.' },
                { num:'04', title:'Transparent collaboration',    desc:'Regular updates, clear timelines, and open communication throughout every phase of your product.' },
              ].map((item,i) => (
                <div key={item.num} style={{ display:'flex', gap:20, alignItems:'flex-start', paddingTop:i>0?24:0, paddingBottom:i<3?24:0, borderBottom:i<3?'1px solid rgba(10,10,20,0.08)':'none', opacity:whyVisible?1:0, transform:whyVisible?'none':'translateY(16px)', transition:`opacity 500ms ${i*80+160}ms, transform 500ms ${i*80+160}ms` }}>
                  <span style={{ fontFamily:"'Anton', sans-serif", fontSize:10, color: i%2===0 ? 'var(--blue)' : 'var(--red)', flexShrink:0, paddingTop:3 }}>{item.num}</span>
                  <div>
                    <div style={{ fontSize:15, fontWeight:700, color:'var(--ink)', marginBottom:6, lineHeight:1.3 }}>{item.title}</div>
                    <div style={{ fontSize:14, lineHeight:1.72, color:'var(--ink-soft)' }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      */}

      {/* ══════════════ CTA — same dark gradient-card theme used across the site ══════════════ */}
      <section style={{ padding:'clamp(56px,7vw,88px) 0 clamp(24px,3vw,40px)' }}>
        <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
          <div ref={ctaRef} style={{
            background:'linear-gradient(135deg, var(--ink) 0%, #1a1a2e 100%)',
            borderRadius:28,
            padding:'clamp(48px,6vw,88px) clamp(32px,5vw,80px)',
            textAlign:'center', position:'relative', overflow:'hidden',
            opacity:ctaVisible?1:0, transform:ctaVisible?'none':'translateY(24px)',
            transition:'opacity 520ms, transform 520ms',
          }}>
            <div aria-hidden style={{
              position:'absolute', inset:0, opacity:0.5,
              background:'radial-gradient(ellipse at 25% 50%, rgba(255,0,4,0.28), transparent 55%), radial-gradient(ellipse at 78% 50%, rgba(0,60,255,0.18), transparent 55%)',
            }}/>
            <div style={{ position:'relative' }}>
              <span style={{ fontFamily:"'Inter', sans-serif", fontWeight:700, fontSize:10.5, letterSpacing:3, color:'rgba(255,255,255,0.35)', textTransform:'uppercase' }}>START YOUR PROJECT</span>
              <p style={{ fontFamily:"'Anton', sans-serif", fontWeight:400, fontSize:'clamp(28px,4.5vw,58px)', color:'white', lineHeight:1.2, margin:'14px 0 16px', }}>
                Ready to build your <span style={{ color:'#ff4d4d' }}>next product?</span>
              </p>
              <p style={{ fontSize:16, color:'rgba(255,255,255,0.5)', maxWidth:440, margin:'0 auto 40px' }}>
                Tell us about your idea. Our team will evaluate your requirements and get back to you quickly.
              </p>
              <Link to="/contact" className="hoverable" style={{
                display:'inline-flex', alignItems:'center', gap:12,
                padding:'17px 40px', background:'linear-gradient(135deg, var(--red), #cc0003)', color:'white',
                borderRadius:999, fontSize:14, fontWeight:700,
                boxShadow:'0 18px 48px rgba(255,0,4,0.38)',
                transition:'transform 200ms ease, box-shadow 200ms ease',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 24px 56px rgba(255,0,4,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)';   e.currentTarget.style.boxShadow='0 18px 48px rgba(255,0,4,0.38)'; }}
              >
                Get In Touch <ArrowIcon size={13}/>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer/>
    </>
  );
}
